/**
 * Currency utility functions
 * Handles currency code and symbol mapping, and retrieving saved currency settings
 */

// Currency code to symbol mapping
const CURRENCY_SYMBOLS = {
  'INR': '₹',
  'USD': '$',
  'GBP': '£',
  'EUR': '€',
  'AUD': 'A$',
  'CAD': 'C$',
  'JPY': '¥',
  'CNY': '¥',
  'SGD': 'S$',
  'AED': 'د.إ',
  'MYR': 'RM'
};

/**
 * Get the saved currency from localStorage
 * @returns {string} Currency code (default: 'INR')
 */
export const getSavedCurrency = () => {
  try {
    const savedSettings = JSON.parse(localStorage.getItem('regionalSettings') || '{}');
    return savedSettings.currency || 'INR';
  } catch (error) {
    console.error('Error reading saved currency:', error);
    return 'INR';
  }
};

/**
 * Get currency symbol for a given currency code
 * @param {string} currencyCode - Currency code (e.g., 'INR', 'USD')
 * @returns {string} Currency symbol (e.g., '₹', '$')
 */
export const getCurrencySymbol = (currencyCode = null) => {
  const code = currencyCode || getSavedCurrency();
  return CURRENCY_SYMBOLS[code] || code || '₹';
};

/**
 * Format price with currency symbol
 * @param {number|string} price - Price value
 * @param {string} currencyCode - Optional currency code (uses saved currency if not provided)
 * @returns {string} Formatted price string (e.g., '₹1,234.56')
 */
export const formatPrice = (price, currencyCode = null) => {
  const symbol = getCurrencySymbol(currencyCode);
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  if (isNaN(numericPrice)) {
    return `${symbol}0`;
  }
  
  // Show exact decimal values without rounding
  // Check if the number has decimal places
  const priceStr = numericPrice.toString();
  const hasDecimals = priceStr.includes('.');
  
  if (hasDecimals) {
    // Show all decimal places (preserve exact decimals, up to reasonable limit of 4 for currency)
    const decimalPlaces = Math.min((priceStr.split('.')[1] || '').length, 4);
    return `${symbol}${numericPrice.toLocaleString('en-IN', { 
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces 
    })}`;
  } else {
    // No decimals, show as integer (no .00 forced)
    return `${symbol}${numericPrice.toLocaleString('en-IN', { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    })}`;
  }
};

/**
 * Get currency code and symbol together
 * @param {string} currencyCode - Optional currency code (uses saved currency if not provided)
 * @returns {Object} Object with code and symbol
 */
export const getCurrencyInfo = (currencyCode = null) => {
  const code = currencyCode || getSavedCurrency();
  return {
    code,
    symbol: getCurrencySymbol(code)
  };
};


